import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import BoxContainer from '../../style-components/BoxContainer.jsx';
import Loading from '../../style-components/Loading.jsx';
import HeaderContainer from '../../style-components/HeaderContainer.jsx';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TableButton,
} from '../Custom-UI/Tables/Elements.jsx';
import Pagination from '../Custom-UI/Tables/Pagination.jsx';
import { useGetLearningPaths, deleteLearningPath } from '../../models/learning_path.js';
import { useGetCourse } from '../../models/course.js';
import { useGetCurrentUser } from '../../models/user.js';

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const teacherName = teacher => {
  if (!teacher) return '-';
  return `${teacher.firstnames ?? ''} ${teacher.lastnames ?? ''}`.trim() || '-';
};

function LearningPathListAll() {
  const navigate = useNavigate();
  const { id: courseId } = useParams();

  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [learningPaths, setLearningPaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: itemsPerPage,
    totalPages: 1,
  });

  const [user, userLoading] = useGetCurrentUser();
  const [course] = useGetCourse(courseId);
  const [data, dataLoading] = useGetLearningPaths(courseId, page, itemsPerPage);

  useEffect(() => {
    if (data && !dataLoading) {
      setLearningPaths(data.data ?? []);
      setPagination({
        currentPage: data.pagination?.page ?? 1,
        itemsPerPage: itemsPerPage,
        totalPages: data.pagination?.totalPages ?? 1,
      });
      setLoading(false);
    }
  }, [data, dataLoading, itemsPerPage]);

  const handleSearch = option => {
    setSearchValue(option.target.value.toLowerCase());
  };

  const handleDelete = async learningPathId => {
    const confirmation = window.confirm(
      '¿Estás seguro que quieres eliminar esta ruta de aprendizaje?'
    );
    if (confirmation) {
      await deleteLearningPath(courseId, learningPathId);
      window.location.reload();
    }
  };

  const goToView = learningPathId => {
    navigate(`/courses/${courseId}/learning-paths/${learningPathId}`);
  };

  const gotToEdit = learningPathId => {
    navigate(`/courses/${courseId}/learning-paths/${learningPathId}/edit`);
  };

  const breadcrumb = {
    items: [
      { name: 'Cursos', href: '/courses' },
      { name: course?.name ?? 'Curso', href: '/courses' },
      { name: 'Rutas de Aprendizaje', href: '' },
    ],
    maxItems: 3,
  };

  const actions = [
    {
      title: 'Añadir Ruta',
      icon: <AddIcon />,
      handleClick: () => navigate(`/courses/${courseId}/learning-paths/create`),
    },
  ];

  const filteredPaths = learningPaths.filter(path =>
    (path.title ?? '').toLowerCase().includes(searchValue)
  );

  return (
    <BoxContainer>
      <div className='antialiased font-sans w-full'>
        <div className='divide-y divide-primary'>
          <HeaderContainer
            breadcrumb={breadcrumb}
            title='Rutas de Aprendizaje'
            search={{ handleSearch: handleSearch }}
            actions={actions}
          />
          <div className='py-8 px-4 sm:px-8 min-w-full w-full'>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className='min-w-72 w-72'>Título</TableHeader>
                  <TableHeader>Creado por</TableHeader>
                  <TableHeader>Fecha de creación</TableHeader>
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                {dataLoading || loading || userLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <div className='flex justify-center items-center h-full'>
                        <Loading />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredPaths.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <div className='flex justify-center items-center py-6 text-muted-foreground'>
                        No hay rutas de aprendizaje para este curso.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPaths.map(path => (
                    <TableRow key={path.id}>
                      <TableCell type='name'>
                        <div className='font-bold text-base text-primary text-left'>
                          {path.title}
                        </div>
                      </TableCell>
                      <TableCell>{teacherName(path.teacher)}</TableCell>
                      <TableCell>{formatDate(path.created_at)}</TableCell>
                      <TableCell>
                        <div className='flex w-full justify-end items-center gap-2'>
                          <TableButton variant='view' onClick={() => goToView(path.id)}>
                            <VisibilityOutlinedIcon />
                          </TableButton>
                          {(user?.id === path.teacher?.id || user?.role === 'ADMIN') && (
                            <>
                              <TableButton
                                variant='edit'
                                onClick={() => {
                                  gotToEdit(path.id);
                                }}>
                                <EditOutlinedIcon />
                              </TableButton>
                              <TableButton variant='delete' onClick={() => handleDelete(path.id)}>
                                <DeleteOutlinedIcon color='red' />
                              </TableButton>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <Pagination
              pagination={pagination}
              setPage={setPage}
              setLoading={setLoading}
              setItemPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>
    </BoxContainer>
  );
}

export default LearningPathListAll;
